import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { AccessToken, User, Workspace, Organization, Token, Member, Role, Limits, ThemeConfig, SidebarItem } from "@/models/account";
import { API_CROWLINGO } from "./core";

// ===== User APIs =====
export const login = (email: string, password: string) =>
  API_CROWLINGO.post<AccessToken>("users/login/", {
    username: email,
    password: password,
  });

export const useUser = () => useApiQuery<User>({ url: "/users/me" }, { staleTime: 5 * 60 * 1000 });

export const useUsers = () => useApiQuery<User[]>({ url: "/users/" });

export const useUserById = (id: string) => useApiQuery<User>({ url: `/users/${id}` });

export const useCreateUser = () =>
  useApiMutation<User, Partial<User>>({
    url: "/users/",
    method: "POST",
  });

export const useUpdateUser = () =>
  useApiMutation<User, Partial<User> & { id: string }, { id: string }>({
    url: "/users/{id}",
    method: "PATCH",
  });

export const useDeleteUser = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/users/{id}",
    method: "DELETE",
  });

export const useUpdateUserStatus = () =>
  useApiMutation<User, { id: string; status: string }, { id: string }>({
    url: "/users/{id}",
    method: "PATCH",
  });

export const useUpdateUserLimits = () =>
  useApiMutation<User, { id: string; limits: Partial<Limits> }, { id: string }>({
    url: "/users/{id}",
    method: "PATCH",
  });

export const useChangePassword = () =>
  useApiMutation<any, { password: string }>({
    url: "/users/password",
    method: "PATCH",
  });

export const useForgotPassword = () =>
  useApiMutation<any, { mail: string }>({
    url: "/users/forgot-password",
    method: "POST",
  });

export const useActivateUser = () =>
  useApiMutation<User, { tmp_token: string }>({
    url: "/users/activate",
    method: "POST",
  });

// ===== Organization APIs =====
export const useOrganizations = () =>
  useApiQuery<Organization[]>({ url: "/organizations/" }, { staleTime: 5 * 60 * 1000 });

export const useOrganization = (id: string) =>
  useApiQuery<Organization>({ url: `/organizations/${id}`, enabled: !!id }, { staleTime: 5 * 60 * 1000 });

export const useCreateOrganization = () =>
  useApiMutation<Organization, { name: string; admin_email: string; admin_password: string; admin_name?: string }>({
    url: "/organizations/",
    method: "POST",
  });

export const useUpdateOrganization = () =>
  useApiMutation<Organization, Partial<Organization> & { id: string }, { id: string }>({
    url: "/organizations/{id}",
    method: "PATCH",
  });

export const useDeleteOrganization = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/organizations/{id}",
    method: "DELETE",
  });

// ===== Token APIs =====
export const useTokens = () => useApiQuery<Token[]>({ url: "/tokens/" }, { staleTime: 5 * 60 * 1000 });

export const useToken = (id: string) =>
  useApiQuery<Token>({ url: `/tokens/${id}`, enabled: !!id });

export const useCreateToken = () =>
  useApiMutation<Token, Partial<Token>>({
    url: "/tokens/",
    method: "POST",
  });

export const useUpdateToken = () =>
  useApiMutation<Token, Partial<Token>, { id: string }>({
    url: "/tokens/{id}",
    method: "PATCH",
  });

export const useDeleteToken = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/tokens/{id}",
    method: "DELETE",
  });

// ===== Member APIs =====
export const useMembers = () => useApiQuery<Member[]>({ url: "/members/" }, { staleTime: 5 * 60 * 1000 });

export const useMember = (id: string) =>
  useApiQuery<Member>({ url: `/members/${id}`, enabled: !!id });

export const useCreateMember = () =>
  useApiMutation<Member, Partial<Member>>({
    url: "/members/",
    method: "POST",
  });

export const useUpdateMember = () =>
  useApiMutation<Member, { id: string; role: Role | string }, { id: string }>({
    url: "/members/{id}",
    method: "PATCH",
  });

export const useDeleteMember = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/members/{id}",
    method: "DELETE",
  });

// ===== Workspace APIs =====
export const useWorkspaces = () =>
  useApiQuery<Workspace[]>({ url: "/workspaces/" }, { staleTime: 5 * 60 * 1000 });

export const useWorkspace = (id: string) =>
  useApiQuery<Workspace>({ url: `/workspaces/${id}`, enabled: !!id });

export const useCreateWorkspace = () =>
  useApiMutation<Workspace, { name: string; description?: string }>({
    url: "/workspaces/",
    method: "POST",
  });

export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
  limits?: Partial<Limits>;
  theme_config?: ThemeConfig | null;
  // Sidebar configuration
  sidebar_items?: SidebarItem[];
  sidebar_width?: number;
  sidebar_show_favorites?: boolean;
  sidebar_compact_mode?: boolean;
  sidebar_locked?: boolean;
  sidebar_expanded_items?: string[];
}

export const useUpdateWorkspace = () =>
  useApiMutation<Workspace, UpdateWorkspaceRequest, { id: string }>({
    url: "/workspaces/{id}",
    method: "PATCH",
  });

export const useDeleteWorkspace = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/workspaces/{id}",
    method: "DELETE",
  });

// ===== Workspace Members APIs =====
export const useWorkspaceMembers = (workspaceId: string) =>
  useApiQuery<Member[]>({ 
    url: `/members/?workspace=${workspaceId}`,
    enabled: !!workspaceId
  });

export const useCreateWorkspaceMember = () =>
  useApiMutation<Member, { user: string; workspace: string; role: Role | string }>({
    url: "/members/",
    method: "POST",
  });

export const useUpdateWorkspaceMember = () =>
  useApiMutation<Member, { id: string; role: Role | string }, { id: string }>({
    url: "/members/{id}",
    method: "PATCH",
  });

export const useDeleteWorkspaceMember = () =>
  useApiMutation<void, {}, { id: string }>({
    url: "/members/{id}",
    method: "DELETE",
  });

// ===== Impersonation APIs =====
export interface ImpersonationResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  impersonated_user: {
    id: string;
    email: string;
    name: string;
  };
}

export const useImpersonate = () =>
  useApiMutation<ImpersonationResponse, {}, { id: string }>({
    url: "/users/{id}/impersonate",
    method: "POST",
  });

